import { useEffect, useState } from "react";
import { toast } from "sonner";
import { ImagePlus, Trash2 } from "lucide-react";
import { Button } from "@/components/ui/button";
import { Input } from "@/components/ui/input";
import { Label } from "@/components/ui/label";
import { Textarea } from "@/components/ui/textarea";
import { PageHeader, Panel } from "@/components/common";
import { api, errMsg } from "@/lib/api";

const KEYS = ["upi_id", "payee_name", "qr_image", "bank_name", "account_name", "account_number", "ifsc", "instructions"];

export default function Settings() {
  const [f, setF] = useState(Object.fromEntries(KEYS.map((k) => [k, ""])));
  const set = (k) => (e) => setF({ ...f, [k]: e.target.value });

  useEffect(() => {
    api.get("/admin/settings/payment").then((r) => setF((p) => ({ ...p, ...Object.fromEntries(KEYS.map((k) => [k, r.data[k] || ""])) })));
  }, []);

  const pickQr = (file) => {
    if (!file) return;
    if (file.size > 1_000_000) return toast.error("QR image must be under 1 MB");
    const reader = new FileReader();
    reader.onload = () => setF((p) => ({ ...p, qr_image: reader.result }));
    reader.readAsDataURL(file);
  };

  const save = async () => {
    try {
      await api.put("/admin/settings/payment", f);
      toast.success("Payment settings saved");
    } catch (e) {
      toast.error(errMsg(e));
    }
  };

  const inp = (k, label) => <div><Label>{label}</Label><Input className="mt-1.5" value={f[k]} onChange={set(k)} data-testid={`settings-${k.replace("_", "-")}-input`} /></div>;
  return (
    <div>
      <PageHeader eyebrow="Configuration" title="Payment settings" subtitle="Shown to agencies when they pay for a plan or credit package."
        actions={<Button onClick={save} data-testid="settings-save-button">Save settings</Button>} />
      <div className="grid lg:grid-cols-2 gap-6 items-start">
        <Panel title="UPI">
          <div className="space-y-4">
            {inp("upi_id", "UPI ID")}
            {inp("payee_name", "Payee name")}
            <div>
              <Label>Payment QR code</Label>
              <div className="mt-2 flex items-center gap-4">
                {f.qr_image ? <img src={f.qr_image} alt="QR" className="h-32 w-32 object-contain rounded-lg border bg-white" data-testid="settings-qr-preview" />
                  : <div className="h-32 w-32 rounded-lg border border-dashed flex items-center justify-center text-xs text-muted-foreground">No QR</div>}
                <div className="flex flex-col gap-2">
                  <Button variant="outline" size="sm" asChild><label className="cursor-pointer"><ImagePlus className="h-4 w-4 mr-1" />Upload<input type="file" accept="image/*" hidden onChange={(e) => pickQr(e.target.files[0])} data-testid="settings-qr-upload" /></label></Button>
                  {f.qr_image && <Button variant="ghost" size="sm" onClick={() => setF({ ...f, qr_image: "" })}><Trash2 className="h-4 w-4 mr-1" />Remove</Button>}
                </div>
              </div>
            </div>
          </div>
        </Panel>
        <Panel title="Bank transfer">
          <div className="space-y-4">
            {inp("bank_name", "Bank name")}
            {inp("account_name", "Account holder name")}
            <div className="grid grid-cols-2 gap-4">{inp("account_number", "Account number")}{inp("ifsc", "IFSC")}</div>
            <div><Label>Instructions for agencies</Label><Textarea rows={3} className="mt-1.5" value={f.instructions} onChange={set("instructions")} data-testid="settings-instructions-input" /></div>
          </div>
        </Panel>
      </div>
    </div>
  );
}
